import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';
import HumanCheck from './HumanCheck';

export default function LoginPage() {
  const [step, setStep] = useState(1);  // 1 = creds, 2 = OTP
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [verified, setVerified] = useState(false);
  const [honeypot, setHoneypot] = useState('');   // bots fill this
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [emailMasked, setEmailMasked] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const formOpenedAt = useRef(Date.now());
  const { loginInit, loginVerify, formatError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Show a small notice if redirected here by idle-logout.
  useEffect(() => {
    if (params.get('reason') === 'idle') {
      setInfo('Signed out for inactivity. Please sign in again.');
    }
  }, [params]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!verified) { setError('Please verify you are human.'); return; }
    setLoading(true);
    try {
      const data = await loginInit({
        email,
        password,
        human_verified: verified,
        honeypot,
        elapsed_ms: Date.now() - formOpenedAt.current,
      });
      // Branch on whether the user has 2FA on or off.
      if (data.requires_otp === false || data.access_token) {
        // 2FA OFF — AuthContext.loginInit already stored tokens + set the user.
        try {
          const meResp = await api.get('/auth/me');
          if (meResp.data.role === 'super_admin') { navigate('/admin'); return; }
        } catch { /* ignore */ }
        navigate('/dashboard');
        return;
      }
      // 2FA ON — show OTP entry step.
      setChallenge(data.challenge);
      setEmailMasked(data.email_masked || email);
      setStep(2);
      setResendIn(30);
      setInfo(`We emailed a 6-digit code to ${data.email_masked || email}.`);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!code || code.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setLoading(true);
    try {
      await loginVerify({ challenge, code });
      // Determine destination role-based.
      try {
        const meResp = await api.get('/auth/me');
        if (meResp.data.role === 'super_admin') { navigate('/admin'); return; }
      } catch { /* ignore */ }
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || loading) return;
    setError(''); setInfo(''); setLoading(true);
    try {
      const data = await loginInit({
        email,
        password,
        human_verified: verified,
        honeypot,
        elapsed_ms: Date.now() - formOpenedAt.current,
      });
      setChallenge(data.challenge);
      setEmailMasked(data.email_masked || email);
      setResendIn(30);
      setInfo(`New code sent to ${data.email_masked || email}.`);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setCode('');
    setChallenge(null);
    setError('');
    setInfo('');
  };

  return (
    <div className="min-h-screen relative bg-black overflow-hidden flex items-center justify-center px-4 py-10 sm:px-6 sm:py-12">
      <div className="pointer-events-none absolute inset-0 opacity-50"
           style={{ background: 'radial-gradient(45% 45% at 50% 35%, rgba(37,99,235,0.18) 0%, rgba(0,0,0,0) 60%)' }} />

      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="font-heading font-semibold text-white text-xl inline-block">NextBell</Link>
        </div>

        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-7 sm:p-9 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.45)]">
          {step === 1 ? (
            <>
              <h1 data-testid="login-page-title" className="font-heading text-2xl sm:text-[28px] font-semibold text-white mb-1.5 tracking-tight">Sign in</h1>
              <p className="text-slate-500 text-sm mb-6">Welcome back. Sign in to your dashboard.</p>

              <form onSubmit={submitCreds} className="space-y-4" data-testid="login-form" autoComplete="on">
                {error && <Banner tone="error" testid="login-error">{error}</Banner>}
                {info && <Banner tone="info">{info}</Banner>}

                <Field label="Email">
                  <input data-testid="login-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    className={INPUT_DARK}
                    autoComplete="email" inputMode="email"
                    placeholder="you@company.com" />
                </Field>

                <Field label="Password">
                  <div className="relative">
                    <input data-testid="login-password-input" type={showPass ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                      className={`${INPUT_DARK} pr-11`}
                      autoComplete="current-password"
                      placeholder="Enter your password" />
                    <button type="button" onClick={() => setShowPass(!showPass)} aria-label="Toggle password visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <HumanCheck verified={verified} onChange={setVerified} />

                {/* Honeypot — invisible to humans, irresistible to bots. */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
                  <label>Phone</label>
                  <input type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
                </div>

                <button data-testid="login-submit-btn" type="submit" disabled={loading || !verified}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-full px-6 py-3 transition-all disabled:opacity-50 mt-2 shadow-[0_0_30px_rgba(37,99,235,0.35)]">
                  {loading ? 'Sending code…' : 'Continue'}
                </button>
              </form>

              <div className="mt-6 space-y-2 text-center">
                <p className="text-sm text-slate-400">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-blue-400 hover:text-blue-300 font-medium">Start free trial</Link>
                </p>
                <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-slate-300">Forgot your password?</Link>
              </div>
            </>
          ) : (
            <>
              <button onClick={goBack} type="button" className="text-slate-400 hover:text-white text-xs flex items-center gap-1 mb-4" data-testid="otp-back-btn">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <div className="flex items-center gap-3 mb-1.5">
                <div className="w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                  <ShieldCheck className="w-4.5 h-4.5 text-blue-400" />
                </div>
                <h1 className="font-heading text-2xl sm:text-[28px] font-semibold text-white tracking-tight">Check your email</h1>
              </div>
              <p className="text-slate-500 text-sm mb-6">We sent a 6-digit code to <span className="text-slate-300">{emailMasked}</span>. It expires in 10 minutes.</p>

              <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
                {error && <Banner tone="error" testid="otp-error">{error}</Banner>}
                {info && <Banner tone="info">{info}</Banner>}

                <Field label="6-digit code">
                  <input
                    data-testid="otp-code-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className={`${INPUT_DARK} text-center tracking-[0.6em] text-lg font-mono`}
                  />
                </Field>

                <button data-testid="otp-submit-btn" type="submit" disabled={loading || code.length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-full px-6 py-3 transition-all disabled:opacity-50 shadow-[0_0_30px_rgba(37,99,235,0.35)]">
                  {loading ? 'Verifying…' : 'Sign in'}
                </button>

                <button type="button" onClick={resend} disabled={resendIn > 0 || loading} data-testid="otp-resend-btn"
                  className="w-full text-sm text-slate-400 hover:text-white disabled:text-slate-600 disabled:cursor-not-allowed">
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-[11px] text-slate-600 text-center mt-7">
          By signing in you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}

// Use text-base (16px) on mobile to prevent iOS auto-zoom; text-sm on sm+ for compact layout.
const INPUT_DARK = "w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-white text-base sm:text-sm focus:border-blue-500 focus:bg-white/[0.07] focus:ring-1 focus:ring-blue-500/40 transition-all placeholder:text-slate-600";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Banner({ tone = 'error', children, testid }) {
  const styles = tone === 'error'
    ? 'bg-red-500/10 border-red-500/30 text-red-300'
    : 'bg-blue-500/10 border-blue-500/30 text-blue-200';
  return (
    <div data-testid={testid} className={`border rounded-xl px-4 py-3 text-sm ${styles}`}>{children}</div>
  );
}
